import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Modal, ReactTable, toast } from "@vilocnv/allsetra-core";
import { Box, Chip, Typography, useTheme } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { ObjectsService } from "app/data/services";
import AddMaintenanceAlertRuleModal, {
  MaintenanceAlertRuleModalData,
  MaintenanceAlertRuleSaveResult
} from "components/common/objects/ObjectTabsLayout/AddMaintenanceAlertRuleModal";
import MaintenanceAlertObjectsListModal from "./MaintenanceAlertObjectsListModal";
import type { MaintenanceAlertRule } from "./maintenanceAlertRuleTypes";
import { MAINTENANCE_ALERT_RULES_UPDATED_EVENT } from "./maintenanceAlertsEvents";

const getNumberFormatter = (language: string) =>
  new Intl.NumberFormat(language.startsWith("nl") ? "nl-NL" : "en-US");

const OBJECTS_LIST_CHIP_SX = {
  maxWidth: 220,
  backgroundColor: "#F4F3FF",
  color: "#6941C6",
  fontWeight: 500,
  "& .MuiChip-label": {
    px: 1.25
  },
  "&:hover": {
    backgroundColor: "#EBE9FE"
  }
} as const;

export type MaintenanceAlertsTableVariant = "account" | "object";

export type MaintenanceAlertsTableSectionProps = {
  accountId: string;
  variant: MaintenanceAlertsTableVariant;
  /** Required when variant is "object" */
  objectId?: string;
};

const MaintenanceAlertsTableSection: FC<MaintenanceAlertsTableSectionProps> = ({
  accountId,
  variant,
  objectId
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation([
    "translation",
    "tableHeadingsTranslation"
  ]);

  const numberFormatter = useMemo(
    () => getNumberFormatter(i18n.language),
    [i18n.language]
  );
  const hoursUnit = t("maintenanceAlerts.hoursUnit");

  const [maintenanceAlerts, setMaintenanceAlerts] = useState<
    MaintenanceAlertRule[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRule, setSelectedRule] = useState<MaintenanceAlertRule | null>(
    null
  );
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [allAccountObjects, setAllAccountObjects] = useState<
    Array<{ uniqueId: string; name: string }>
  >([]);
  const [objectsListModal, setObjectsListModal] = useState<{
    alertName: string;
    objectNames: string[];
  } | null>(null);

  const fetchMaintenanceAlerts = useCallback(async () => {
    if (!accountId) return;
    if (variant === "object" && !objectId) return;

    try {
      setIsLoading(true);
      const response =
        variant === "account"
          ? await ObjectsService.getAccountMaintenanceAlertRules(accountId)
          : await ObjectsService.getObjectMaintenanceAlertRules(
              accountId,
              objectId as string
            );
      setMaintenanceAlerts(response.data || []);
    } catch {
      toast.error(t("maintenanceAlerts.toasts.loadError"));
    } finally {
      setIsLoading(false);
    }
  }, [accountId, objectId, variant, t]);

  useEffect(() => {
    fetchMaintenanceAlerts();
  }, [fetchMaintenanceAlerts]);

  useEffect(() => {
    const fetchAllObjects = async () => {
      if (!accountId) return;
      try {
        const response = await ObjectsService.getAllBasicInfoObjects(accountId);
        setAllAccountObjects(response?.data || []);
      } catch {
        setAllAccountObjects([]);
      }
    };

    fetchAllObjects();
  }, [accountId]);

  const objectsById = useMemo(
    () =>
      new Map(
        (allAccountObjects || []).map((object) => [
          object.uniqueId,
          object.name
        ])
      ),
    [allAccountObjects]
  );

  const tableData = useMemo(() => maintenanceAlerts || [], [maintenanceAlerts]);

  const isAllObjectsRule = useCallback(
    (row: MaintenanceAlertRule) =>
      Boolean(
        row.selectAllObjects ||
          row.objectIds?.length === 0 ||
          (allAccountObjects.length > 0 &&
            row.objectIds?.length === allAccountObjects.length)
      ),
    [allAccountObjects.length]
  );

  const getObjectNames = useCallback(
    (row: MaintenanceAlertRule) => {
      if (isAllObjectsRule(row)) {
        return allAccountObjects.map((object) => object.name);
      }

      return (row.objectIds || [])
        .map((id) => objectsById.get(id))
        .filter(Boolean) as string[];
    },
    [allAccountObjects, isAllObjectsRule, objectsById]
  );

  const applyOptimisticRuleSave = useCallback(
    ({ action, rule }: MaintenanceAlertRuleSaveResult) => {
      setMaintenanceAlerts((previous) => {
        if (action === "create") {
          const exists = previous.some(
            (item) => item.uniqueId === rule.uniqueId
          );
          return exists ? previous : [...previous, rule];
        }

        return previous.map((item) =>
          item.uniqueId === rule.uniqueId ? { ...item, ...rule } : item
        );
      });
    },
    []
  );

  const applyOptimisticRuleDelete = useCallback((ruleId: string) => {
    setMaintenanceAlerts((previous) =>
      previous.filter((item) => item.uniqueId !== ruleId)
    );
  }, []);

  const handleRuleSaved = useCallback(
    (result: MaintenanceAlertRuleSaveResult) => {
      applyOptimisticRuleSave(result);
    },
    [applyOptimisticRuleSave]
  );

  useEffect(() => {
    if (variant !== "object") return;

    const handler = (event: Event) => {
      const detail = (event as CustomEvent<MaintenanceAlertRuleSaveResult>)
        .detail;
      if (detail?.rule) {
        handleRuleSaved(detail);
        return;
      }
      fetchMaintenanceAlerts();
    };

    window.addEventListener(MAINTENANCE_ALERT_RULES_UPDATED_EVENT, handler);
    return () => {
      window.removeEventListener(
        MAINTENANCE_ALERT_RULES_UPDATED_EVENT,
        handler
      );
    };
  }, [fetchMaintenanceAlerts, handleRuleSaved, variant]);

  const columns = useMemo(
    () => [
      {
        name: t("tableHeading.name", { ns: "tableHeadingsTranslation" }),
        selector: (row: MaintenanceAlertRule) => (
          <Typography
            variant="body2"
            sx={{
              fontSize: "inherit",
              fontWeight: "inherit",
              color: "inherit"
            }}
          >
            {row.name || "N/A"}
          </Typography>
        ),
        sortable: true,
        sortField: "name",
        wrap: true
      },
      {
        name: t("tableHeading.metric", { ns: "tableHeadingsTranslation" }),
        selector: (row: MaintenanceAlertRule) => (
          <Badge colorScheme="info">
            {row.metric === 1
              ? t("maintenanceAlerts.contactHours")
              : t("maintenanceAlerts.runtimeHours")}
          </Badge>
        ),
        width: "180px"
      },
      {
        name: t("tableHeading.firstTrigger", {
          ns: "tableHeadingsTranslation"
        }),
        selector: (row: MaintenanceAlertRule) =>
          `${numberFormatter.format(row.firstTrigger || 0)} ${hoursUnit}`,
        sortable: true,
        sortField: "firstTrigger"
      },
      {
        name: t("tableHeading.interval", { ns: "tableHeadingsTranslation" }),
        selector: (row: MaintenanceAlertRule) =>
          `${numberFormatter.format(row.interval || 0)} ${hoursUnit}`,
        sortable: true,
        sortField: "interval"
      },
      {
        name: t("tableHeading.objects", { ns: "tableHeadingsTranslation" }),
        selector: (row: MaintenanceAlertRule) => {
          if (isAllObjectsRule(row)) {
            return (
              <Chip
                size="small"
                label={t("maintenanceAlerts.allObjectsChip")}
                sx={OBJECTS_LIST_CHIP_SX}
              />
            );
          }

          const objectNames = getObjectNames(row);
          const ruleObjectCount = row.objectIds?.length ?? 0;

          if (!objectNames.length) {
            if (ruleObjectCount === 0) return "0";
            // Names couldn't be resolved (e.g. allAccountObjects fetch failed);
            // fall back to the count from the rule so the table doesn't misleadingly show "0".
            return (
              <Chip
                size="small"
                label={t("maintenanceAlerts.objectsChipLabel", {
                  count: ruleObjectCount
                })}
                sx={OBJECTS_LIST_CHIP_SX}
              />
            );
          }

          const chipLabel =
            objectNames.length === 1
              ? objectNames[0]
              : t("maintenanceAlerts.objectsChipLabel", {
                  count: objectNames.length
                });

          const isObjectsChipClickable = objectNames.length > 1;

          return (
            <Chip
              size="small"
              label={chipLabel}
              clickable={isObjectsChipClickable}
              onClick={
                isObjectsChipClickable
                  ? () =>
                      setObjectsListModal({
                        alertName: row.name || "",
                        objectNames
                      })
                  : undefined
              }
              sx={OBJECTS_LIST_CHIP_SX}
            />
          );
        }
      }
    ],
    [t, getObjectNames, hoursUnit, isAllObjectsRule, numberFormatter]
  );

  const primaryButton =
    variant === "account"
      ? {
          id: "create-maintenance-alert",
          text: t("maintenanceAlerts.createMaintenanceAlert"),
          variant: "contained" as const,
          theme,
          onHoverBgColor: "#00998C",
          startIcon: <AddIcon />,
          onClick: () => setIsCreateModalOpen(true)
        }
      : undefined;

  const tableMainBoxStyles =
    variant === "account"
      ? {
          height: "calc(100vh - 360px)",
          border: "1px solid #D5D9E8",
          borderRadius: "10px",
          marginBottom: "5vh",
          padding: "1px"
        }
      : {
          height: "calc(100vh - 314px)",
          border: "1px solid #D5D9E8",
          borderRadius: "10px",
          marginBottom: "10vh",
          padding: "1px",
          "& tbody": {
            "& tr": {
              "& td": {
                padding: "10px 16px"
              }
            }
          }
        };

  const rootBoxSx = variant === "account" ? { mt: 2, mx: 2 } : { mt: 4 };

  const maintenanceTableModuleName =
    variant === "account"
      ? "maintenance-alerts-account-expanded"
      : "maintenance-alerts-object-detail";

  return (
    <Box sx={rootBoxSx}>
      <ReactTable
        columns={columns}
        data={tableData}
        isSelectable={false}
        isLoading={isLoading}
        paginationTotalRows={tableData.length}
        paginationServer={false}
        sx={undefined}
        translator={t}
        moduleName={maintenanceTableModuleName}
        primaryButton={primaryButton}
        tableMainBoxStyles={tableMainBoxStyles}
        mainTableStyles={{
          height: tableData?.length > 0 ? "fit-content" : "100%"
        }}
        cellActions={[
          {
            name: t("buttonsText.edit"),
            onClick: (row: MaintenanceAlertRule) => {
              setSelectedRule(row);
              setIsEditModalOpen(true);
            }
          },
          {
            name: t("buttonsText.delete"),
            onClick: (row: MaintenanceAlertRule) => {
              setSelectedRule(row);
              setIsDeleteModalOpen(true);
            }
          }
        ]}
      />
      {variant === "account" ? (
        <AddMaintenanceAlertRuleModal
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          accountId={accountId}
          defaultSelectAllObjects
          onSuccess={handleRuleSaved}
        />
      ) : null}
      <AddMaintenanceAlertRuleModal
        open={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedRule(null);
        }}
        accountId={accountId}
        mode="edit"
        initialRule={
          selectedRule
            ? ({
                uniqueId: selectedRule.uniqueId,
                name: selectedRule.name,
                description: selectedRule.description || "",
                metric: selectedRule.metric,
                firstTrigger: selectedRule.firstTrigger,
                interval: selectedRule.interval,
                recipients: selectedRule.recipients || [],
                objectIds: selectedRule.objectIds || []
              } as MaintenanceAlertRuleModalData)
            : null
        }
        onSuccess={(result) => {
          handleRuleSaved(result);
          setSelectedRule(null);
        }}
      />
      <MaintenanceAlertObjectsListModal
        open={!!objectsListModal}
        onClose={() => setObjectsListModal(null)}
        alertName={objectsListModal?.alertName || ""}
        objectNames={objectsListModal?.objectNames || []}
      />
      <Modal
        open={isDeleteModalOpen}
        onClose={() => {
          if (isDeleting) return;
          setIsDeleteModalOpen(false);
          setSelectedRule(null);
        }}
        title={t("maintenanceAlerts.deleteTitle")}
        theme={theme}
        secondaryBtnProps={{
          text: t("buttonsText.cancel"),
          onClick: () => {
            setIsDeleteModalOpen(false);
            setSelectedRule(null);
          }
        }}
        primaryBtnProps={{
          text: t("buttonsText.delete"),
          loading: isDeleting,
          onClick: async () => {
            if (!selectedRule?.uniqueId || !accountId) return;
            try {
              setIsDeleting(true);
              await ObjectsService.deleteMaintenanceAlertRule(
                accountId,
                selectedRule.uniqueId
              );
              toast.success(t("maintenanceAlerts.toasts.deleteSuccess"));
              applyOptimisticRuleDelete(selectedRule.uniqueId);
              setIsDeleteModalOpen(false);
              setSelectedRule(null);
            } catch {
              toast.error(t("maintenanceAlerts.toasts.deleteError"));
            } finally {
              setIsDeleting(false);
            }
          }
        }}
      >
        <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: variant === "account" ? "16px" : "12px",
              backgroundColor: "#FEF3F2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <WarningAmberRoundedIcon
              sx={{ color: "#D92D20", fontSize: 30, mb: 1 }}
            />
          </Box>
          {t("maintenanceAlerts.deleteSubtitle", {
            name: selectedRule?.name || ""
          })}
        </Box>
      </Modal>
    </Box>
  );
};

export default MaintenanceAlertsTableSection;
